import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import SearchSelect from "../ui-components/SearchSelect";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import Button from "@ui-components/Button";
import { TrashIcon } from "@heroicons/react/20/solid";
import {KColorProductsDependenciesEditor} from "./KColorProductsDependenciesEditor";


/**
 * The component is used to manage the Kcolor question type in the configurator with the following conditions:
 * - list of families to exclude
 * - list of products to exclude
 * - list of colors to exclude
 * - exceptions allowed on family-color
 *
 * @param {newData: KcolorDaraSchema, setNewData: function} props
 * @returns {JSX.Element}
 */
const ServiceConfiguratorBlockInputKColor = ({
  newData,
  setNewData,
  editable,
  setShowDependencies,
  block,
  questions,
  recursiveQuestions,
  outputs,
  data,
  type,
  service,
  ...rest
}) => {
  const apiRequest = useCountryAxiosInstance();
  const intl = useIntl();
  const [showKColorProductsDependenciesEditor, setShowKColorProductsDependenciesEditor] = useState(false);
  const [kColorProducts, setKColorProducts] = useState([]);
  const [productColorsCache, setProductColorsCache] = useState({});

  useEffect(async () => {
    const response = await apiRequest.get("/kcolors/products", {
      params: {
        only_active: false,
        only_with_pricebook: false,
      },
    })
    setKColorProducts(response.map((v) => ({
      cod_prod_comm : v.cod_prod_comm,
      des_prod_comm : v.des_prod_comm,
      id: v.id,
      lst_min_qty: v.lst_min_qty,
      base_um: v.base_um,
      active: v.active,
      is_in_pricebook: v.is_in_pricebook,
    })));
  }, [])

  useEffect(() => {
    // Whenever product-color exceptions change, ensure that all products have their colors loaded
    const current = newData.metadata_?.colorsException
      ?.map((v) => v.product?.value)
      .filter(Boolean)
      || [];
    const toLoad = current.filter((v) => !!v && !(v in productColorsCache));
    if (toLoad.length === 0) return;
    Promise.all(
      toLoad.map((productId) =>
        apiRequest.get(`/kcolors/products/${productId}`)
      )
    ).then((responses) => {
      const newCache = { ...productColorsCache };
      responses.forEach((response, index) => {
        const productId = toLoad[index];
        newCache[productId] = response.lst_available_colors || [];
      });
      setProductColorsCache(newCache);
    });
  }, [newData.metadata_?.colorsException]);

  const updateMetadata = (key, value) => {
    setNewData((prev) => ({
      ...prev,
      metadata_: {
        ...(prev.metadata_ || {}),
        [key]: value,
      },
    }));
  };

  const handleColorsExceptionsChange = (updatedExceptions) => {
    updateMetadata("colorsException", updatedExceptions);
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Button
          onClick={() =>
            setShowKColorProductsDependenciesEditor(true)
          }
        >
          {intl.formatMessage({
            id: "edit_kcolor_products_dependencies",
            defaultMessage: " KColor products dependencies",
          })}
        </Button>
      </div>
      <Button
        disabled={!editable}
        className="mt-4"
        onClick={() => {
          const newException = { product: null, colors: [] };
          const updatedExceptions = [
            ...(newData.metadata_?.colorsException || []),
            newException,
          ];
          handleColorsExceptionsChange(updatedExceptions);
        }}
      >
        {intl.formatMessage({
          id: "add_product_colors_exception",
          defaultMessage: "Add product colors exception",
        })}
      </Button>
      {newData.metadata_?.colorsException?.map((ex, i) => (
        <div className="mt-6" key={i}>
          <div className="flex flex-row justify-between">
            <p className="mb-4">Eccezione {i + 1}</p>
            <TrashIcon
              disabled={!editable}
              className="h-5 w-5 text-red-500 cursor-pointer"
              onClick={() => {
                const updatedExceptions =
                  newData.metadata_.colorsException.filter(
                    (_, index) => index !== i
                  );
                handleColorsExceptionsChange(updatedExceptions);
              }}
            />
          </div>
          <SearchSelect
            isDisabled={!editable}
            label={intl.formatMessage({
              id: "select_products_to_add_exception",
              defaultMessage: "Select product",
            })}
            options={kColorProducts.map((v) => ({
              label: v.des_prod_comm,
              value: v.id,
              kColorProduct: v,
            }))}
            value={ex.product}
            isClearable={true}
            onChange={(product) => {
              const updatedExceptions = newData.metadata_.colorsException.map(
                (v, index) => (index === i ? { ...v, product } : v)
              );
              handleColorsExceptionsChange(updatedExceptions);
            }}
          />
          {ex.product && <SearchSelect
            isDisabled={!editable}
            isMulti={true}
            label={intl.formatMessage({id: "select_colors_toexclude", defaultMessage: "Select colors to exclude"})}
            options={(productColorsCache[ex.product.value] || []).map((value) => ({
              label: value,
              value: value,
            }))}
            value={ex.colors}
            onChange={(colors) => {
              const updatedExceptions = newData.metadata_.colorsException.map(
                (v, index) => (index === i ? { ...v, colors } : v)
              );
              handleColorsExceptionsChange(updatedExceptions);
            }}
          />}
        </div>
      ))}
      {
        showKColorProductsDependenciesEditor && (
          <KColorProductsDependenciesEditor
            onExit={() => setShowKColorProductsDependenciesEditor(false)}
            KColorProducts={kColorProducts}
            setShowDependencies={setShowDependencies}
            block={block}
            questions={questions}
            recursiveQuestions={recursiveQuestions}
            outputs={outputs}
            data={data}
            type={'dependencies'}
            service={service}
            newData={newData.metadata_?.products_dependencies || {}}
            setNewData={(nv) => {
              const updatedMetadata = {
                ...(newData.metadata_ || {}),
                products_dependencies: nv,
              };
              setNewData((prev) => ({
                ...prev,
                metadata_: updatedMetadata,
              }));
            }}
          />
        )
      }
    </div>
  );
};

export default ServiceConfiguratorBlockInputKColor;
